import React from "react";
import { Link } from "react-router-dom";
import { Radio, CalendarClock, Activity, Trophy, ArrowUpRight, Cpu, Layers, Star, Compass, SatelliteDish, Sparkles, Clock, Lock } from "lucide-react";
import { PageHeader } from "@/components/shell/TopBar";
import { MetricCard, IntelligenceCard } from "@/components/cards/Cards";
import { LiveMatchCard } from "@/components/cards/LiveMatchCard";
import { LiveLocked } from "@/components/cards/LiveLocked";
import { PrematchCard } from "@/components/cards/PrematchCard";
import { marketHubRowToFixture } from "@/api/marketHub";
import { AtlasAssistantPanel } from "@/components/atlas/AtlasAssistantPanel";
import { ATLAS_ASSISTANT_ENABLED } from "@/config/features";
import { ResultBadge, MarketBadge } from "@/components/badges/Badges";
import { LoadingCards, LoadingRows, ErrorState, EmptyState } from "@/components/common/States";
import { useOverview, useLive, useTodayPerformance, useFeed, usePrematch, useMarketHub } from "@/hooks/useAtlas";
import { lifecycleOf } from "@/presentation/resultPresentation";
import { visibleFeedRows } from "@/presentation/entitlements";
import { useFollow } from "@/features/follow/FollowContext";
import { useLaunch } from "@/context/LaunchContext";
import { useSession } from "@/context/SessionContext";
import { fmtDuration } from "@/lib/format";
import { useI18n } from "@/i18n";
import { fmtNum, fmtPct, timeAgo } from "@/lib/format";

export default function Dashboard() {
  const { d, lang } = useI18n();
  const ov = useOverview();
  const live = useLive(24);
  const { isAuthenticated } = useSession();
  // Canonical Prematch V2 — EXACT same source + normalizer as /app/prematch.
  const prematch = usePrematch(24, isAuthenticated);
  const hubView = useMarketHub().data;
  const today = useTodayPerformance();
  const feed = useFeed(8);
  const { followed } = useFollow();
  const { plan, isTrial, secondsRemaining, planLabel, hasPlan } = useLaunch();
  const canLive = hasPlan("PRO");

  const liveRows = (live.data?.rows || []).filter((r) => lifecycleOf(r.status, r.won) === "PENDING").slice(0, 4);
  const recentResolved = (live.data?.rows || []).filter((r) => lifecycleOf(r.status, r.won) !== "PENDING").slice(0, 4);
  // Prematch stats derived from the canonical V2 dataset (same semantics as the
  // Prematch page): candidates = fixtures carrying markets, pending = PENDING.
  // Prematch teaser from the CANONICAL Market Hub inventory (same owner as
  // /app/prematch); legacy only enriches by fixture_id. Falls back to legacy
  // when Market Hub is unavailable (unauthenticated / error / no data).
  const legacyPm = prematch.data?.rows || [];
  const pmRows = (isAuthenticated && hubView)
    ? (() => {
        const byId = new Map(legacyPm.map((r) => [String(r.fixture_id), r]));
        return (hubView.prematch || []).map((h) => marketHubRowToFixture(h, byId.get(String(h.fixture_id))));
      })()
    : legacyPm;
  const pmCandidates = pmRows.filter((f) => (f.markets?.length || 0) > 0).length;
  const pmPending = pmRows.filter((f) => lifecycleOf(f.status, f.won) === "PENDING").length;
  const nextPicks = pmRows.filter((f) => lifecycleOf(f.status, f.won) === "PENDING").slice(0, 2);
  // FREE must not see CURRENT/PENDING signal rows in "Son Hareketler" (live or
  // prematch recommendation leak). Resolved historical rows remain for everyone.
  const feedRows = visibleFeedRows(feed.data?.rows || [], canLive);
  const o = ov.data;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={d.nav.dashboard}
        subtitle={lang === "tr" ? "Canlı izleme, maç öncesi seçimler ve son sonuçlar tek yerde." : "Live monitoring, prematch picks and recent results in one place."}
        live
      />

      {plan && (
        <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="dash-plan-strip">
          <span className="rounded-full border border-atlas/25 bg-atlas/10 px-3 py-1 text-xs font-bold text-atlas-300" data-testid="dash-plan-chip">Plan: {planLabel(lang) ?? plan ?? "—"}</span>
          {isTrial && secondsRemaining != null && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/25 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold-300" data-testid="dash-trial-chip">
              <Clock className="h-3 w-3" /> {lang === "tr" ? `${fmtDuration(secondsRemaining, "tr")} kaldı` : `${fmtDuration(secondsRemaining, "en")} left`}
            </span>
          )}
        </div>
      )}

      {hubView && (hubView.prematchCount > 0 || hubView.liveCount > 0 || hubView.watchingCount > 0) && (
        <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="atlas-active-strip">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-atlas/30 bg-atlas/10 px-3 py-1 text-xs font-bold text-atlas-300">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-atlas" /> {lang === "tr" ? "ATLAS Aktif" : "ATLAS Active"}
          </span>
          {hubView.prematchCount > 0 && <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-foreground/80" data-testid="atlas-prematch-count">{hubView.prematchCount} {lang === "tr" ? "aktif prematch marketi" : "active prematch markets"}</span>}
          {canLive && hubView.liveCount > 0 && <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-foreground/80" data-testid="atlas-live-count">{hubView.liveCount} {lang === "tr" ? "canlı maç izleniyor" : "live matches watched"}</span>}
          {hubView.watchingCount > 0 && <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-foreground/80" data-testid="atlas-watching-count">{hubView.watchingCount} {lang === "tr" ? "senaryo ATLAS tarafından izleniyor" : "scenarios ATLAS is watching"}</span>}
        </div>
      )}

      {plan === "FREE" && !isTrial && (
        <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-atlas/25 bg-gradient-to-br from-atlas/[0.08] to-transparent p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="dash-free-teaser">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-atlas/15 text-atlas-300"><Lock className="h-5 w-5" /></span>
            <div>
              <p className="text-sm font-bold text-foreground">{lang === "tr" ? "SDIP AI'ın tam değerini aç" : "Unlock the full value of SDIP AI"}</p>
              <p className="mt-1 max-w-md text-xs text-muted-foreground">{lang === "tr" ? "Gerçek zamanlı canlı sinyaller, maç öncesi seçimler ve ATLAS Asistan. 3 gün tam ELITE erişimi dene — kart gerekmez." : "Real-time live signals, prematch picks and the ATLAS Assistant. Try full ELITE access for 3 days — no card required."}</p>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <Link to="/app/membership" data-testid="dash-trial-cta" className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-atlas-500 to-atlas-600 px-4 py-2.5 text-sm font-bold text-atlas-ink transition hover:shadow-glow"><Sparkles className="h-4 w-4" /> {lang === "tr" ? "3 Gün Ücretsiz Dene" : "Start 3-Day Trial"}</Link>
            <Link to="/app/membership" className="inline-flex items-center rounded-full border border-white/12 px-4 py-2.5 text-sm font-semibold text-foreground transition hover:border-white/25">{lang === "tr" ? "Planını Yükselt" : "Upgrade"}</Link>
          </div>
        </div>
      )}

      {plan === "FREE" && hubView && (hubView.prematchCount > 0 || hubView.watchingCount > 0) && (
        <div className="mb-6 grid gap-3 sm:grid-cols-3" data-testid="free-value-blocks">
          <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4">
            <p className="text-sm font-bold text-foreground">{lang === "tr" ? `Bugün ${hubView.prematchCount} ATLAS prematch değerlendirmesi` : `${hubView.prematchCount} ATLAS prematch evaluations today`}</p>
            <p className="mt-1 text-xs text-muted-foreground">{lang === "tr" ? "ATLAS bu maçları değerlendiriyor." : "ATLAS is evaluating these matches."}</p>
          </div>
          <Link to="/app/membership" className="rounded-2xl border border-gold/25 bg-gold/[0.06] p-4 transition hover:bg-gold/10" data-testid="free-lock-alt">
            <p className="flex items-center gap-2 text-sm font-bold text-foreground"><Lock className="h-4 w-4 text-gold-400" /> {lang === "tr" ? "Alternatif seçimler seni bekliyor" : "Alternative picks await"}</p>
            <p className="mt-1 text-xs text-muted-foreground">PRO {lang === "tr" ? "ile aç" : "unlock"}</p>
          </Link>
          <Link to="/app/membership" className="rounded-2xl border border-gold/25 bg-gold/[0.06] p-4 transition hover:bg-gold/10" data-testid="free-lock-watch">
            <p className="flex items-center gap-2 text-sm font-bold text-foreground"><Lock className="h-4 w-4 text-gold-400" /> {lang === "tr" ? `ATLAS ${hubView.watchingCount} senaryoyu izliyor` : `ATLAS watching ${hubView.watchingCount} scenarios`}</p>
            <p className="mt-1 text-xs text-muted-foreground">{lang === "tr" ? "xG + Form + H2H · ELITE ile aç" : "xG + Form + H2H · unlock with ELITE"}</p>
          </Link>
        </div>
      )}

      {ov.isLoading ? <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">{[0,1,2,3].map(i=><div key={i} className="skeleton h-28 rounded-2xl" />)}</div>
        : ov.isError || !o ? <ErrorState error={ov.error as any} onRetry={ov.refetch} />
        : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard index={0} label={lang === "tr" ? "Aktif Canlı Sinyal" : "Active LIVE Signals"} value={canLive ? fmtNum(o.live.pending) : "—"} accent="live" icon={canLive ? <Radio className="h-4 w-4" /> : <Lock className="h-4 w-4" />} sub={canLive ? (lang === "tr" ? "şu an bekliyor" : "pending now") : (lang === "tr" ? "PRO ile aç" : "unlock with PRO")} />
            <MetricCard index={1} label={lang === "tr" ? "Sonuçlanan (Canlı)" : "Resolved (LIVE)"} value={fmtNum(o.live.resolved)} accent="atlas" icon={<Activity className="h-4 w-4" />} sub={`${fmtNum(o.live.total)} ${lang === "tr" ? "toplam" : "total"}`} />
            <MetricCard index={2} label={lang === "tr" ? "Takip Ettiğin Maç" : "Matches Followed"} value={fmtNum(followed.length)} accent="gold" icon={<Star className="h-4 w-4" />} sub={lang === "tr" ? "senin listen" : "your list"} />
            <MetricCard index={3} label={lang === "tr" ? "Maç Öncesi Aday" : "Prematch Candidates"} value={fmtNum(pmCandidates)} accent="neutral" icon={<Layers className="h-4 w-4" />} sub={`${fmtNum(pmPending)} ${lang === "tr" ? "bekliyor" : "pending"}`} />
          </div>
        )}

      <div className="mt-6 grid gap-6 xl:grid-cols-[1.6fr_1fr]">
        <div className="min-w-0 space-y-6">
          {/* live opportunities OR monitoring context when 0 signals */}
          <IntelligenceCard
            title={<span className="inline-flex items-center gap-2"><Radio className="h-4 w-4 text-live-400" /> {lang === "tr" ? "Canlı Fırsatlar" : "LIVE Opportunities"}</span>}
            action={<Link to="/app/live" data-testid="dash-live-all" className="inline-flex items-center gap-1 text-xs font-semibold text-atlas-300">{d.common.viewAll} <ArrowUpRight className="h-3.5 w-3.5" /></Link>}
          >
            {!canLive ? <LiveLocked compact teaserCount={hubView?.liveTeaserCount ?? null} />
              : live.isLoading ? <LoadingCards count={2} /> : live.isError ? <ErrorState error={live.error as any} onRetry={live.refetch} />
              : liveRows.length > 0 ? <div className="grid gap-4 sm:grid-cols-2">{liveRows.map((s, i) => <LiveMatchCard key={s.id} s={s} index={i} />)}</div>
              : (
                <div data-testid="dash-monitoring">
                  <div className="flex items-start gap-3 rounded-xl border border-white/8 bg-white/[0.02] p-4">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-atlas/10 text-atlas-400"><SatelliteDish className="h-5 w-5" /></span>
                    <div><p className="text-sm font-semibold text-foreground">{d.live.monitoringTitle}</p><p className="mt-1 text-xs text-muted-foreground">{d.live.monitoringBody}</p></div>
                  </div>
                  {recentResolved.length > 0 && (
                    <>
                      <p className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{lang === "tr" ? "Son sonuçlananlar" : "Recently resolved"}</p>
                      <div className="space-y-2">
                        {recentResolved.map((s) => (
                          <Link key={s.id} to={`/app/match/${s.fixture_id}`} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5 transition hover:border-atlas/25" data-testid="dash-resolved-row">
                            <div className="min-w-0"><p className="truncate text-sm font-medium text-foreground">{s.home_team} <span className="text-muted-foreground">vs</span> {s.away_team}</p><div className="mt-1"><MarketBadge code={s.market_code} backendLabel={s.market_name} size="sm" /></div></div>
                            <ResultBadge status={s.status} won={s.won} size="sm" />
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
          </IntelligenceCard>

          {/* next prematch candidates */}
          <IntelligenceCard
            title={<span className="inline-flex items-center gap-2"><Compass className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Yaklaşan Seçimler" : "Upcoming Picks"}</span>}
            action={<Link to="/app/prematch" className="inline-flex items-center gap-1 text-xs font-semibold text-atlas-300">{d.common.viewAll} <ArrowUpRight className="h-3.5 w-3.5" /></Link>}
          >
            {prematch.isLoading ? <LoadingCards count={2} /> : nextPicks.length === 0 ? <EmptyState title={d.states.emptyPrematch} icon={<CalendarClock className="h-6 w-6" />} />
              : <div className="grid gap-4 sm:grid-cols-2">{nextPicks.map((f, i) => <PrematchCard key={f.fixture_key} f={f} index={i} />)}</div>}
          </IntelligenceCard>

          {/* recent feed */}
          <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Activity className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Son Hareketler" : "Recent Activity"}</span>}>
            {feed.isLoading ? <LoadingRows count={5} /> : feed.isError ? <ErrorState error={feed.error as any} onRetry={feed.refetch} />
              : feedRows.length === 0 ? <EmptyState title={d.states.emptyHistory} />
              : (
                <div className="space-y-2">
                  {feedRows.map((r, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5" data-testid="feed-row">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{r.match}</p>
                        <p className="text-[11px] text-muted-foreground">{timeAgo(r.time, lang)}</p>
                      </div>
                      <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
                        <MarketBadge code={r.market} size="sm" />
                        <ResultBadge status={r.status} won={r.won} size="sm" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </IntelligenceCard>
        </div>

        {/* right column */}
        <div className="min-w-0 space-y-6">
          {/* followed matches */}
          {followed.length > 0 && (
            <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Star className="h-4 w-4 text-gold-400" /> {lang === "tr" ? "Takip Ettiğin Maçlar" : "Followed Matches"}</span>}>
              <div className="space-y-2">
                {followed.slice(0, 5).map((m) => (
                  <Link key={m.fixtureId} to={`/app/match/${m.fixtureId}`} className="flex items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5 transition hover:border-gold/25" data-testid="dash-followed-row">
                    <p className="truncate text-sm font-medium text-foreground">{m.label}</p>
                    <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            </IntelligenceCard>
          )}

          {/* today performance */}
          <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Trophy className="h-4 w-4 text-gold-400" /> {lang === "tr" ? "Bugünkü Performans" : "Today's Performance"}</span>}>
            {today.isLoading ? <LoadingRows count={3} /> : today.isError || !today.data ? <ErrorState error={today.error as any} onRetry={today.refetch} />
              : (
                <div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-xl border border-atlas/20 bg-atlas/5 p-3 text-center"><p className="num text-xl font-bold text-atlas-400">{today.data.data.wins}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{d.result.win}</p></div>
                    <div className="rounded-xl border border-live/20 bg-live/5 p-3 text-center"><p className="num text-xl font-bold text-live-400">{today.data.data.losses}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{d.result.lose}</p></div>
                    <div className="rounded-xl border border-gold/20 bg-gold/5 p-3 text-center"><p className="num text-xl font-bold text-gold-400">{today.data.data.pending}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{d.result.pending}</p></div>
                  </div>
                  <div className="mt-3 space-y-2">
                    {today.data.data.markets.map((m) => (
                      <div key={m.market_code} className="flex items-center justify-between gap-2">
                        <MarketBadge code={m.market_code} size="sm" />
                        <span className="num text-xs text-muted-foreground"><b className="text-foreground/90">{fmtPct(m.hit_rate, 0)}</b> · {m.samples}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-[11px] text-muted-foreground">{lang === "tr" ? "Yalnızca gerçek sonuçlanan verilerden hesaplanır." : "Computed only from real resolved data."}</p>
                </div>
              )}
          </IntelligenceCard>

          {ATLAS_ASSISTANT_ENABLED && <AtlasAssistantPanel compact />}
        </div>
      </div>
    </div>
  );
}
